import PreviousQuestionButton from "./header/PreviousQuestionButton";
import Question from "./question/Question";
import "./application.scss";
import Summary from "./templates/Summary";
import Overlay from "components/ui/loading-spinners/Overlay/Overlay";
import { useGetAnalyticsEventDataByEventTypeQuery, useGetApplicationByIdQuery, useSetApplicationStepAnswerMutation } from "./applicationService";
import { useSelector, useDispatch } from "react-redux";
import { getCurrentUserApplicationId, setLogout, setNhApplicationStatus, getDealApplicationId, getIsInterimDashboard, getCurrentUserId } from "features/authSlice";
import { getStepData } from "pages/application/question/questionSlice";
import { useNavigate } from "react-router-dom";
import usePerformRouting from "features/PerformRouting";
import { useCallback, useEffect, useState } from "react";
import Transition from "./transition/Transition";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { useCreateNeedsMutation } from "pages/dashboard/home/dashboardService";
import { analyticsSections, trackFSInitAppSubmittedEvent, trackFSLOAssignedEvent, trackFSSummaryEvent } from "utilities/full-story";
import LoanOfficerCard from "components/v2/loan-officer-card/LoanOfficerCard";
import useDesktopChecker from "hooks/useDesktopChecker";
import { useLoanOfficer } from "hooks/useLoanOfficer";
import ExitSurvey from "pages/survey/exit/ExitSurvey";
import RedesignAnnouncementModal from "pages/announcements/re-design/re-design-modal";

const ApplicationContainer = () => {
  const stepData = useSelector(getStepData);
  const applicationId = useSelector(getCurrentUserApplicationId);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isFetching, isError: isGetApplicationByIdError, error: getApplicationByIdError } = useGetApplicationByIdQuery(applicationId);
  const [_, { isLoading, isError: isSetApplicationStepAnswerError, error: setApplicationStepAnswerError }] = useSetApplicationStepAnswerMutation({
    fixedCacheKey: "updateApplication",
  });

  const { loanOfficer, isSuccess } = useLoanOfficer();
  const [createNeeds] = useCreateNeedsMutation();
  const dealApplicationId = useSelector(getDealApplicationId);
  const isInterimDashboard = useSelector(getIsInterimDashboard);
  const userId = useSelector(getCurrentUserId);

  useEffect(() => {
    if (isSuccess && loanOfficer) {
      trackFSLOAssignedEvent(loanOfficer, stepData);
    }
  }, [loanOfficer, isSuccess, stepData]);

  const isDesktop = useDesktopChecker();
  const { performRouting } = usePerformRouting();
  const [shouldNavigateToTraditionalDashboard, setShouldNavigateToTraditionalDashboard] = useState(false);
  const applicationIdForAnalytics = useSelector(getCurrentUserApplicationId);

  const isLastStep = stepData.stepCodeText === "EndOfFlow" && stepData.progress === 100;
  const isQuickPathLastStep = isLastStep && stepData.section === "Acknowledgements";
  const analyticsSection = analyticsSections.find((s) => s.section === "Acknowledgements")!;
  const eventType = analyticsSection?.eventType;

  const { data: analyticsData } = useGetAnalyticsEventDataByEventTypeQuery(
    { applicationId: applicationIdForAnalytics, eventType },
    { skip: !isQuickPathLastStep }
  );

  useEffect(() => {
    if (isQuickPathLastStep && analyticsData) {
      trackFSSummaryEvent(analyticsSection.displayName, analyticsData);
    }
  }, [isQuickPathLastStep, analyticsData, analyticsSection]);

  useEffect(() => {
    if (isLastStep) {
      trackFSInitAppSubmittedEvent();
    }
  }, [isLastStep]);

  useEffect(() => {
    performRouting();
  }, []);

  useEffect(() => {
    if (shouldNavigateToTraditionalDashboard) {
      const navTimer = setTimeout(() => {
        navigate("/dashboard"); //navigate to dashboard after min of 20seconds (18seconds transition + 2000ms in this timeout)
      }, 2000);
      return () => clearTimeout(navTimer);
    }
  }, [shouldNavigateToTraditionalDashboard, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isFetching, isLoading]);

  const handleTransitionComplete = useCallback(async () => {
    try {
      await createNeeds({ dealApplicationId, userId, isInterimDashboard });
    } finally {
      setShouldNavigateToTraditionalDashboard(true);
    }
  }, [createNeeds, dealApplicationId, userId, isInterimDashboard]);

  function getApplicationStepAnswerError() {
    if (
      typeof setApplicationStepAnswerError === "object" &&
      "status" in setApplicationStepAnswerError &&
      setApplicationStepAnswerError.status === 401
    ) {
      dispatch(setLogout());
      navigate("/", { state: { previousPath: "/application" } });
    }
  }
  function getApplicationIdError() {
    if (typeof getApplicationByIdError === "object" && "status" in getApplicationByIdError && getApplicationByIdError.status === 401) {
      dispatch(setLogout());
      navigate("/", { state: { previousPath: "/application" } });
    }
  }
  if (isGetApplicationByIdError) {
    getApplicationIdError();
  } else if (isSetApplicationStepAnswerError) {
    getApplicationStepAnswerError();
  }
  if (stepData.stepCodeText === "EndOfFlow") {
    const nhApplicationStatus = "Completed";
    dispatch(setNhApplicationStatus(nhApplicationStatus));

    return (
      <div className="loader-dashboard">
        <Transition onComplete={handleTransitionComplete} />
      </div>
    );
  }

  if (isFetching || isLoading) {
    return (
      <>
        <div className="loading-container">
          <Overlay text="Loading your application" subtext="This will just take a moment!" displayFullScreen={false} />
        </div>
      </>
    );
  }

  if (!stepData) {
    throw new Error("No data");
  }
  const isSummary = stepData?.stepType === "summary";

  return (
    <>
      <RedesignAnnouncementModal />
      <div className="application">
        {featureFlag?.exitSurveyEnabled && <ExitSurvey stepCode={stepData.stepCodeText} />}
        <div className={`application__body ${isSummary ? "application__body--summary" : ""}`}>
          {isDesktop ? (
            <div className="application--desktop-content">
              <div className="application--desktop-content__main">
                {!isSummary && <PreviousQuestionButton />}
                {isSummary ? <Summary /> : <Question />}
              </div>

              <div className="application--desktop-content__loan-officer">
                <LoanOfficerCard loInfo={loanOfficer} />
              </div>
            </div>
          ) : (
            <div className="application--mobile-content">
              {!isSummary && <PreviousQuestionButton />}
              {isSummary ? <Summary /> : <Question />}

              <div className="application--mobile-content__loan-officer">
                <LoanOfficerCard loInfo={loanOfficer} />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ApplicationContainer;
